import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NotificationPanel from '../../components/ui/NotificationPanel';
import StatCard from './components/StatCard';
import QuickActionCard from './components/QuickActionCard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useOrgLogo } from '../../hooks/useOrgLogo';
import TrialBanner from '../../components/ui/TrialBanner';
import TrialExpiredModal from '../../components/ui/TrialExpiredModal';
import useTrialStatus from '../../hooks/useTrialStatus';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [escalationAlerts, setEscalationAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { logoUrl } = useOrgLogo();
  const { trialDaysLeft, isTrialExpired, isSuspended, showPaymentPrompt, checkTrialStatus } = useTrialStatus();

  // All-user stats
  const [stats, setStats] = useState({
    totalTickets: 0,
    openTickets: 0,
    scheduledToday: 0,
    totalQuotes: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [upcomingSchedule, setUpcomingSchedule] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [duplicatingQuoteId, setDuplicatingQuoteId] = useState(null);
  const [quoteSuccessMsg, setQuoteSuccessMsg] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      await Promise.all([
        fetchStats(),
        fetchRecentTickets(),
        fetchUpcomingSchedule(),
        fetchEscalationAlerts(),
        fetchRecentQuotes(),
      ]);
    } catch (err) {
      console.error('Dashboard load error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const todayEnd = new Date(today);
      todayEnd?.setHours(23, 59, 59, 999);

      const [ticketsRes, quotesRes, scheduledRes] = await Promise.all([
        supabase?.from('tickets')?.select('id, status:ticket_statuses(name)', { count: 'exact' }),
        supabase?.from('quotes')?.select('id', { count: 'exact' }),
        supabase?.from('tickets')?.select('id', { count: 'exact' })
          ?.gte('scheduled_date', today?.toISOString())
          ?.lte('scheduled_date', todayEnd?.toISOString())
      ]);

      const allTickets = ticketsRes?.data || [];
      const openTickets = allTickets?.filter(t => {
        const s = t?.status?.name?.toLowerCase();
        return s !== 'completed' && s !== 'cancelled';
      });

      setStats({
        totalTickets: ticketsRes?.count || allTickets?.length,
        openTickets: openTickets?.length,
        scheduledToday: scheduledRes?.count || 0,
        totalQuotes: quotesRes?.count || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err?.message);
    }
  };

  const fetchRecentTickets = async () => {
    try {
      const { data, error } = await supabase
        ?.from('tickets')
        ?.select(`
          id, ticket_number, scheduled_date, description,
          customer:customers(name),
          status:ticket_statuses(name, color),
          priority:ticket_priorities(name, color),
          service_type:ticket_service_types(name)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setRecentTickets(data || []);
    } catch (err) {
      console.error('Error fetching recent tickets:', err?.message);
    }
  };

  const fetchUpcomingSchedule = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const nextWeek = new Date(today);
      nextWeek?.setDate(nextWeek?.getDate() + 7);

      const { data, error } = await supabase
        ?.from('tickets')
        ?.select(`
          id, ticket_number, scheduled_date, description, estimated_duration,
          customer:customers(name),
          status:ticket_statuses(name, color),
          priority:ticket_priorities(name, color)
        `)
        ?.gte('scheduled_date', today?.toISOString())
        ?.lte('scheduled_date', nextWeek?.toISOString())
        ?.order('scheduled_date', { ascending: true })
        ?.limit(8);

      if (error) throw error;
      setUpcomingSchedule(data || []);
    } catch (err) {
      console.error('Error fetching upcoming schedule:', err?.message);
    }
  };

  const fetchEscalationAlerts = async () => {
    try {
      const now = new Date();
      const { data, error } = await supabase
        ?.from('tickets')
        ?.select(`
          id, ticket_number, scheduled_date, description,
          priority:ticket_priorities(name),
          status:ticket_statuses(name),
          customer:customers(name)
        `)
        ?.not('scheduled_date', 'is', null);

      if (error) throw error;

      const alerts = [];
      data?.forEach((ticket) => {
        const priority = ticket?.priority?.name?.toLowerCase();
        const status = ticket?.status?.name?.toLowerCase();
        if (status === 'completed' || status === 'cancelled') return;

        const scheduledDate = new Date(ticket?.scheduled_date);
        const hoursOverdue = (now - scheduledDate) / (1000 * 60 * 60);

        const thresholds = { critical: 1, high: 4, medium: 24, low: 72 };
        const threshold = thresholds?.[priority] ?? 24;

        if (hoursOverdue >= threshold) {
          const hoursLabel = hoursOverdue < 24
            ? `${Math.floor(hoursOverdue)}h overdue`
            : `${Math.floor(hoursOverdue / 24)}d overdue`;

          alerts?.push({
            id: `escalation-${ticket?.id}`,
            type: priority === 'critical' || priority === 'high' ? 'urgent' : 'schedule',
            title: `Escalation: ${ticket?.ticket_number}`,
            message: `${priority?.charAt(0)?.toUpperCase() + priority?.slice(1)} priority ticket for ${ticket?.customer?.name} is ${hoursLabel}.`,
            timestamp: hoursLabel,
            icon: priority === 'critical' ? 'AlertOctagon' : priority === 'high' ? 'AlertTriangle' : 'Clock',
            actionLabel: 'View Ticket',
            actionPath: '/ticket-management'
          });
        }
      });

      setEscalationAlerts(alerts);
    } catch (error) {
      console.error('Error fetching escalation alerts:', error?.message);
    }
  };

  const fetchRecentQuotes = async () => {
    try {
      const { data, error } = await supabase
        ?.from('quotes')
        ?.select(`
          id, quote_number, notes, expected_service_date, total_amount, created_at,
          customer_id, salesperson_id,
          customer:customers(name),
          salesperson:personnel!quotes_salesperson_id_fkey(name),
          status:quote_statuses(name),
          quote_products(*)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(5);
      if (error) throw error;
      setRecentQuotes(data || []);
    } catch (err) {
      console.error('Error fetching recent quotes:', err?.message);
    }
  };

  const handleDuplicateQuote = async (quote) => {
    try {
      setDuplicatingQuoteId(quote?.id);
      const { data: draftStatus, error: statusError } = await supabase
        ?.from('quote_statuses')
        ?.select('id')
        ?.eq('name', 'Draft')
        ?.single();

      if (statusError || !draftStatus) throw new Error('Draft status not found');

      const { data: newQuote, error: quoteError } = await supabase
        ?.from('quotes')
        ?.insert([{
          customer_id: quote?.customer_id,
          salesperson_id: quote?.salesperson_id,
          status_id: draftStatus?.id,
          notes: quote?.notes,
          expected_service_date: quote?.expected_service_date,
        }])
        ?.select()
        ?.single();

      if (quoteError) throw quoteError;

      if (quote?.quote_products?.length > 0) {
        const products = quote?.quote_products?.map(p => ({
          quote_id: newQuote?.id,
          product_id: p?.product_id || null,
          name: p?.name,
          quantity: p?.quantity,
          price: p?.price,
        }));
        await supabase?.from('quote_products')?.insert(products);
      }

      setQuoteSuccessMsg(`Quote duplicated as ${newQuote?.quote_number} (Draft)`);
      setTimeout(() => setQuoteSuccessMsg(''), 3000);
      await fetchRecentQuotes();
    } catch (err) {
      console.error('Error duplicating quote:', err?.message);
      alert(`Error: ${err?.message}`);
    } finally {
      setDuplicatingQuoteId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return { month: '---', day: '--', time: 'Not scheduled' };
    const d = new Date(dateStr);
    return {
      month: d?.toLocaleString('en-US', { month: 'short' })?.toUpperCase(),
      day: d?.getDate()?.toString(),
      time: d?.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  const getStatusBadgeClass = (statusName) => {
    const s = statusName?.toLowerCase();
    if (s === 'scheduled') return 'bg-blue-100 text-blue-700';
    if (s === 'in progress') return 'bg-yellow-100 text-yellow-700';
    if (s === 'pending') return 'bg-gray-100 text-gray-600';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'approved') return 'bg-green-100 text-green-700';
    if (s === 'draft') return 'bg-gray-100 text-gray-600';
    if (s === 'sent') return 'bg-blue-100 text-blue-700';
    if (s === 'converted') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getPriorityClass = (priorityName) => {
    const p = priorityName?.toLowerCase();
    if (p === 'critical') return 'text-error';
    if (p === 'high') return 'text-warning';
    if (p === 'medium') return 'text-primary';
    return 'text-muted-foreground';
  };

  const notifications = [
    {
      id: 1,
      type: 'urgent',
      title: 'New Urgent Request',
      message: 'Customer reported critical equipment failure requiring immediate attention',
      timestamp: '2 minutes ago',
      icon: 'AlertCircle',
      actionLabel: 'View Request',
      actionPath: '/support-request-management'
    },
    ...escalationAlerts
  ];

  const quickActions = [
    {
      id: 1,
      title: 'New Support Request',
      description: 'Log a new customer service request',
      icon: 'Plus',
      iconColor: 'var(--color-primary)',
      actionLabel: 'Create Request',
      onClick: () => navigate('/support-request-management')
    },
    {
      id: 2,
      title: 'Ticket Management',
      description: 'View and manage all service tickets',
      icon: 'ClipboardList',
      iconColor: 'var(--color-secondary)',
      actionLabel: 'View Tickets',
      onClick: () => navigate('/ticket-management')
    },
    {
      id: 3,
      title: 'Quote Management',
      description: 'Create and manage customer quotes',
      icon: 'FileText',
      iconColor: 'var(--color-warning)',
      actionLabel: 'View Quotes',
      onClick: () => navigate('/quote-management')
    },
    {
      id: 4,
      title: 'Customer Database',
      description: 'Access customer profiles and service history',
      icon: 'Database',
      iconColor: 'var(--color-accent)',
      actionLabel: 'Browse Customers',
      onClick: () => navigate('/customer-database')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <TrialBanner
        trialDaysLeft={trialDaysLeft}
        isTrialExpired={isTrialExpired}
        isSuspended={isSuspended}
        onDismiss={() => {}}
      />
      <TrialExpiredModal
        isOpen={showPaymentPrompt}
        onActivated={checkTrialStatus}
      />
      <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          {logoUrl && (
            <div className="flex items-center mb-4">
              <img src={logoUrl} alt="Organization logo" className="h-14 w-auto object-contain max-w-[220px]" />
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-1">
                Dashboard
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Overview of all operations, tickets, and activity
              </p>
            </div>
            <div className="flex items-center gap-3">
              {escalationAlerts?.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
                  <span className="text-xs font-medium text-error">
                    {escalationAlerts?.length} escalation{escalationAlerts?.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <Button
                variant="default"
                iconName="Bell"
                iconPosition="left"
                onClick={() => setIsNotificationPanelOpen(true)}
                className="w-full sm:w-auto">
                Notifications ({notifications?.length})
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <StatCard
                title="Total Tickets"
                value={stats?.totalTickets?.toString()}
                subtitle="All service tickets"
                icon="ClipboardList"
                iconColor="var(--color-primary)"
                onClick={() => navigate('/ticket-management')}
                trend={0}
                trendDirection="neutral"
              />
              <StatCard
                title="Open Tickets"
                value={stats?.openTickets?.toString()}
                subtitle="Active, non-completed tickets"
                icon="Zap"
                iconColor="var(--color-warning)"
                onClick={() => navigate('/ticket-management')}
                trend={0}
                trendDirection="neutral"
              />
              <StatCard
                title="Scheduled Today"
                value={stats?.scheduledToday?.toString()}
                subtitle="Jobs scheduled for today"
                icon="CalendarCheck"
                iconColor="var(--color-secondary)"
                onClick={() => navigate('/ticket-management')}
                trend={0}
                trendDirection="neutral"
              />
              <StatCard
                title="Total Quotes"
                value={stats?.totalQuotes?.toString()}
                subtitle="All quotes in the system"
                icon="FileText"
                iconColor="var(--color-success)"
                onClick={() => navigate('/quote-management')}
                trend={0}
                trendDirection="neutral"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Left: Recent Tickets + Quick Actions */}
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                {/* Recent Tickets */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">Recent Tickets</h2>
                    <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>
                      View All
                    </Button>
                  </div>
                  {recentTickets?.length === 0 ? (
                    <div className="bg-card rounded-xl border border-border p-8 text-center">
                      <Icon name="ClipboardList" size={32} color="var(--color-muted-foreground)" />
                      <p className="mt-3 text-sm text-muted-foreground">No tickets found.</p>
                    </div>
                  ) : (
                    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Ticket #</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Customer</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Priority</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Scheduled</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {recentTickets?.map(ticket => {
                              const { month, day, time } = formatDate(ticket?.scheduled_date);
                              return (
                                <tr key={ticket?.id} className="hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate('/ticket-management')}>
                                  <td className="px-4 py-3">
                                    <span className="text-sm font-medium text-primary">{ticket?.ticket_number}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-sm text-foreground">{ticket?.customer?.name || '—'}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`text-xs font-medium ${getPriorityClass(ticket?.priority?.name)}`}>
                                      {ticket?.priority?.name || '—'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(ticket?.status?.name)}`}>
                                      {ticket?.status?.name || 'Unknown'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-xs text-muted-foreground">
                                      {ticket?.scheduled_date ? `${month} ${day}, ${time}` : 'Not scheduled'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Recent Quotes */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">Recent Quotes</h2>
                    <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/quote-management')}>
                      View All
                    </Button>
                  </div>
                  {quoteSuccessMsg && (
                    <div className="mb-3 bg-success/10 border border-success/20 rounded-lg px-4 py-2 flex items-center gap-2">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                      <span className="text-sm text-success font-medium">{quoteSuccessMsg}</span>
                    </div>
                  )}
                  {recentQuotes?.length === 0 ? (
                    <div className="bg-card rounded-xl border border-border p-8 text-center">
                      <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
                      <p className="mt-3 text-sm text-muted-foreground">No quotes found.</p>
                    </div>
                  ) : (
                    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Quote #</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Customer</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Amount</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {recentQuotes?.map(quote => (
                              <tr key={quote?.id} className="hover:bg-muted/20 transition-colors">
                                <td className="px-4 py-3">
                                  <span className="text-sm font-medium text-primary cursor-pointer" onClick={() => navigate('/quote-management')}>{quote?.quote_number}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm text-foreground">{quote?.customer?.name || '—'}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm font-semibold text-foreground">${parseFloat(quote?.total_amount || 0)?.toFixed(2)}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(quote?.status?.name)}`}>
                                    {quote?.status?.name || 'Unknown'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => handleDuplicateQuote(quote)}
                                    disabled={duplicatingQuoteId === quote?.id}
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border bg-card hover:bg-muted text-xs font-medium text-foreground transition-colors disabled:opacity-50"
                                    title="Duplicate Quote"
                                  >
                                    {duplicatingQuoteId === quote?.id ? (
                                      <Icon name="Loader2" size={12} color="currentColor" className="animate-spin" />
                                    ) : (
                                      <Icon name="Copy" size={12} color="currentColor" />
                                    )}
                                    Duplicate
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {quickActions?.map(action => (
                      <QuickActionCard key={action?.id} {...action} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Upcoming Schedule + Escalation Alerts */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">Upcoming Schedule (7 days)</h2>
                  <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>
                    View Calendar
                  </Button>
                </div>
                {upcomingSchedule?.length === 0 ? (
                  <div className="bg-card rounded-xl border border-border p-6 text-center">
                    <Icon name="Calendar" size={28} color="var(--color-muted-foreground)" />
                    <p className="mt-2 text-sm text-muted-foreground">No upcoming scheduled jobs in the next 7 days.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingSchedule?.map(ticket => {
                      const { month, day, time } = formatDate(ticket?.scheduled_date);
                      return (
                        <div key={ticket?.id} className="bg-card rounded-xl border border-border p-4 hover:border-primary/40 transition-colors cursor-pointer" onClick={() => navigate('/ticket-management')}>
                          <div className="flex items-start gap-3">
                            <div className="flex flex-col items-center justify-center w-12 flex-shrink-0 bg-primary/5 rounded-lg py-1">
                              <span className="text-xs font-caption text-muted-foreground uppercase">{month}</span>
                              <span className="text-xl font-heading font-semibold text-foreground">{day}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-sm font-medium text-primary">{ticket?.ticket_number}</span>
                                <span className={`text-xs font-medium ${getPriorityClass(ticket?.priority?.name)}`}>
                                  {ticket?.priority?.name?.toUpperCase()}
                                </span>
                              </div>
                              <p className="text-xs text-foreground font-medium mb-1">{ticket?.customer?.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{ticket?.description}</p>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                  <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                                  <span className="text-xs text-muted-foreground">{time}</span>
                                </div>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(ticket?.status?.name)}`}>
                                  {ticket?.status?.name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Escalation Alerts */}
                {escalationAlerts?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-base font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                      Escalation Alerts
                    </h3>
                    <div className="space-y-2">
                      {escalationAlerts?.slice(0, 5)?.map(alert => (
                        <div key={alert?.id} className="bg-error/5 border border-error/20 rounded-lg p-3 cursor-pointer hover:bg-error/10 transition-colors" onClick={() => navigate('/ticket-management')}>
                          <p className="text-xs font-medium text-error mb-1">{alert?.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{alert?.message}</p>
                          <span className="text-xs text-error font-medium">{alert?.timestamp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        notifications={notifications}
      />
    </div>
  );
};

export default Dashboard;